import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  ReactNode,
  ReactElement,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { createClient as createBpApiClient } from "../generated/bp-api/client/client.gen";
import { createClient as createBtxClient } from "../generated/btx/client/client.gen";
import { createClient as createBpbeClient } from "../generated/bpbe/client/client.gen";
import { createClient as createCentroPortalClient } from "../generated/centro-portal/client/client.gen";

import { withUserSafeErrorsForAll } from "./withUserSafeErrors";

import type { AxiosInstance } from "axios";
import type { Client as BpApiClient } from "../generated/bp-api/client/types.gen";
import type { Client as BtxClient } from "../generated/btx/client/types.gen";
import type { Client as BpbeClient } from "../generated/bpbe/client/types.gen";
import type { Client as CentroPortalClient } from "../generated/centro-portal/client/types.gen";

export type { Client as BpApiClient } from "../generated/bp-api/client/types.gen";
export type { Client as BtxClient } from "../generated/btx/client/types.gen";
export type { Client as BpbeClient } from "../generated/bpbe/client/types.gen";
export type { Client as CentroPortalClient } from "../generated/centro-portal/client/types.gen";

interface HeyApiClients {
  bpApiClient: BpApiClient;
  btxClient: BtxClient;
  bpbeClient: BpbeClient;
  centroPortalClient: CentroPortalClient;
}

interface InvalidateQueryFilter {
  tags: string[];
  path?: Record<string, unknown>;
  query?: Record<string, unknown>;
}

interface HeyApiHookResult extends HeyApiClients {
  invalidateQuery: (filter: InvalidateQueryFilter) => Promise<void>;
}

const HeyApiContext = createContext<HeyApiClients | null>(null);

const DEFAULT_BASE_URL = "http://localhost:4200/";

interface HeyApiProviderProps {
  children: ReactNode;
  axiosInstance: AxiosInstance;
}

const buildHeyApiClients = (axiosInstance: AxiosInstance): HeyApiClients => {
  const base = axiosInstance.defaults.baseURL || DEFAULT_BASE_URL;
  return withUserSafeErrorsForAll({
    bpApiClient: createBpApiClient({
      axios: axiosInstance,
      baseURL: `${base}bp/v1`,
    }),
    btxClient: createBtxClient({
      axios: axiosInstance,
      baseURL: `${base}btx/v1`,
    }),
    bpbeClient: createBpbeClient({
      axios: axiosInstance,
      baseURL: `${base}v1`,
    }),
    centroPortalClient: createCentroPortalClient({
      axios: axiosInstance,
      baseURL: `${base}gateway`,
    }),
  });
};

export function HeyApiProvider({
  children,
  axiosInstance,
}: HeyApiProviderProps): ReactElement {
  const clients = useMemo(
    () => buildHeyApiClients(axiosInstance),
    [axiosInstance],
  );

  return (
    <HeyApiContext.Provider value={clients}>{children}</HeyApiContext.Provider>
  );
}

/**
 * Invalidates generated queries by their spec tags, for apps whose clients live
 * outside HeyApiProvider. useHeyApi returns the same function.
 */
export function useInvalidateQuery(): HeyApiHookResult["invalidateQuery"] {
  const queryClient = useQueryClient();

  return useCallback(
    ({ tags, path, query }: InvalidateQueryFilter) =>
      queryClient.invalidateQueries({
        queryKey: [{ tags, ...(path && { path }), ...(query && { query }) }],
      }),
    [queryClient],
  );
}

export function useHeyApi(): HeyApiHookResult {
  const clients = useContext(HeyApiContext);
  if (!clients) {
    throw new Error("useHeyApi must be used within a HeyApiProvider");
  }
  const invalidateQuery = useInvalidateQuery();

  return useMemo(
    () => ({ ...clients, invalidateQuery }),
    [clients, invalidateQuery],
  );
}
